import { cache } from '../../cache-impl/cache-registry';
import { compare } from '../../../../webmodule-common/other/clone';
import { customElement, property, query, state } from 'lit/decorators.js';
import { DashboardWidget } from './dashboard-widget';
import { DateTime } from 'luxon';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid';
import { getApiFactory } from '../../../api/api-injector';
import { html } from 'lit';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { monitor } from '../../../../webmodule-common/other/monitor';
import {
  PurchaseOrderSummary,
  ResultGetPurchaseOrderList,
  Territory,
  TerritoryFranchiseeBranchLink
} from '../../../api/supplier-api-interface-franchiseenetwork';
import { localDateToServer, serverDateTimeToLocal } from '../../../../webmodule-common/other/datetime-converter';
import { Snippet } from '../../../../webmodule-common/interop/webmodule-interop';
import { tlang } from '../../../../webmodule-common/other/language/lang';
import { userDataStore } from '../../common/current-user-data-store';
import {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../webmodule-common/components/src/webmodule-components';
import { DateRange } from './dashboard-filter';

export class DeliveryPipelinePoTablePageCache {
  _pageKey = this.pageKey(-1, -1, '', false, undefined, undefined);
  _pagePromise: Promise<ResultGetPurchaseOrderList | null> | null = null;

  pageKey(
    index: number,
    length: number,
    sortField: string,
    sortAsc: boolean,
    branchIds?: string[],
    assignedToUserId?: string,
    startDate?: string,
    endDate?: string
  ): {
    index: number;
    length: number;
    sortField: string;
    sortAsc: boolean;
    branchIds?: string[];
    assignedToUserId?: string;
    startDate?: string;
    endDate?: string;
  } {
    return { index, length, sortAsc, sortField, branchIds, assignedToUserId, startDate, endDate };
  }

  async getData(
    index: number,
    length: number,
    sortField: string,
    sortAsc: boolean,
    branchIds?: string[],
    assignedToUserId?: string,
    startDate?: string,
    endDate?: string
  ) {
    if (branchIds === undefined) {
      return null;
    }
    const currentPage = this.pageKey(
      index,
      length,
      sortField,
      sortAsc,
      branchIds,
      assignedToUserId,
      startDate,
      endDate
    );
    if (!compare(this._pageKey, currentPage)) {
      this._pageKey = currentPage;
      this._pagePromise = getApiFactory()
        .dataAggregation()
        .getPurchaseOrderList({
          pageIndex: this._pageKey.index,
          pageSize: this._pageKey.length,
          sortField: this._pageKey.sortField,
          sortAsc: this._pageKey.sortAsc,
          branchIds: this._pageKey.branchIds ?? [],
          assignedToUserId: this._pageKey.assignedToUserId ?? emptyGuid,
          startDate: this._pageKey.startDate ?? '',
          endDate: this._pageKey.endDate ?? ''
        });
    }
    return this._pagePromise;
  }
}

@customElement('delivery-pipeline-po-widget')
export class DeliveryPipelinePoWidget extends DashboardWidget {
  @property({ attribute: false })
  public dateRange?: DateRange;

  @property({ type: Array })
  branchIds?: string[] = [];

  @property({ attribute: false })
  public assignedToUserId?: string;

  @property({ attribute: false })
  public cache?: DeliveryPipelinePoTablePageCache;
  @query('#delivery-pipeline-po-widget')
  protected table!: WebModuleTable;
  @state()
  private data: PurchaseOrderSummary[] = [];
  @property({ attribute: false })
  public territoryLinks: TerritoryFranchiseeBranchLink[] = [];
  @property({ attribute: false })
  public territories: Territory[] = [];
  /**
   * Whether the territory lookup data actually arrived. Separate from the arrays being empty, because the
   * two mean opposite things to the reader: an empty list that LOADED says these branches have no territory,
   * an empty list because the call failed says we do not know. Undefined until the first attempt.
   */
  private territoriesLoaded?: boolean;
  private authorCache = new Map<string, string>();
  private branchCache = new Map<string, string>();

  // Re-fetch when the dashboard filters change (the table only fetches itself on
  // first render and on pagination, so a filter change needs an explicit kick).
  async connectedCallback() {
    super.connectedCallback();
    // Retry on the flag rather than on the arrays being empty: a supplier with no territories configured
    // loaded successfully and has nothing to fetch again, whereas a failed load has everything to retry.
    if (this.territoriesLoaded !== true) {
      await this.loadTerritories();
    }
  }

  @monitor(['dateRange', 'branchIds', 'assignedToUserId'])
  refresh() {
    this.table?.fetchEvent();
  }

  getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: tlang`%%v6-reference%%`,
        fieldName: 'reference',
        minWidth: '110px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderSummary;
          //replaced code. reference is not a number. its a qualified formatted
          //string that holds a v6 quote num-pref-suff if v6 is the source
          //use build in function, matches dealer, and inbox
          return isEmptyOrSpace(rowItem.reference) ? tlang`N/A` : html`${rowItem.reference}`;
        }
      },
      {
        title: tlang`%%purchase-order-supplier-system-status%%`,
        fieldName: 'supplierSystemStatus',
        minWidth: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderSummary;

          return html`${rowItem.supplierSystemStatus}`;
        }
      },
      {
        title: tlang`%%franchisee%%`,
        fieldName: 'customerName',
        minWidth: '120px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderSummary;

          return html`${this.lookupFranchiseeName(rowItem.franchiseeTenantId)}`;
        }
      },
      {
        title: tlang`%%Territory%%`,
        fieldName: 'Territory',
        minWidth: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderSummary;
          return html`${this.lookupTerritoryName(rowItem.branchId)}`;
        }
      },
      {
        title: tlang`%%branch%%`,
        fieldName: 'branchName',
        minWidth: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderSummary;
          return html`${this.lookupBranchName(rowItem.branchId)}`;
        }
      },
      {
        title: tlang`%%Author%%`,
        fieldName: 'authorName',
        minWidth: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderSummary;
          return html`${this.lookupAuthorName(rowItem.creationUserId)}`;
        }
      },
      {
        title: tlang`%%purchase-order-description%%`,
        fieldName: 'title',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderSummary;

          return html`${rowItem.title}`;
        }
      },
      {
        title: tlang`%%purchase-order-supplier-delivery-date%%`,
        fieldName: 'supplierSystemDeliveryDate',
        minWidth: '100px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderSummary;
          return rowItem.supplierSystemDeliveryDate == null
            ? ''
            : serverDateTimeToLocal(rowItem.supplierSystemDeliveryDate).toLocaleDateString();
        }
      }
    ];
  }

  lookupFranchiseeName(franchiseeTenantId: number): string {
    return (
      userDataStore.allFranchiseeDetails.tenantList.tenants?.find(x => x.id === franchiseeTenantId)?.companyName ??
      tlang`<unavailable>`
    );
  }
  lookupBranchName(branchId: string): string {
    return this.branchCache.get(branchId) ?? tlang`<unavailable>`;
  }
  /**
   * Three outcomes, not one, because each asks something different of whoever is reading the row.
   *
   * A branch that belongs to no territory is `Unassigned`: an ordinary, expected state, and one the supplier
   * fixes in their own settings. Saying `<unavailable>` there dressed up a normal configuration gap as an
   * error and sent people looking for a fault instead of assigning the branch.
   *
   * Both remaining cases keep `<unavailable>`, and neither may be folded into `Unassigned`. If the territory
   * data never loaded we do not know whether the branch has one, and claiming `Unassigned` for every row on
   * the page would be a confident lie told at exactly the moment we know least. And a link that points at a
   * territory we cannot find is a dangling reference - a data fault worth seeing as one, not an unassigned
   * branch.
   */
  lookupTerritoryName(branchId: string): string {
    if (this.territoriesLoaded !== true) {
      return tlang`<unavailable>`;
    }

    const territoryId = this.territoryLinks.find(link => link.branchId === branchId)?.territoryId;
    if (!territoryId) {
      return tlang`Unassigned`;
    }
    return this.territories.find(t => t.id === territoryId)?.name ?? tlang`<unavailable>`;
  }

  lookupAuthorName(creationUserId: string): string {
    return this.authorCache.get(creationUserId) ?? tlang`<unavailable>`;
  }

  protected getHeader(): Snippet {
    return tlang`Delivery Pipeline`;
  }

  protected isNearDeadlineOrOverdue(data: PurchaseOrderSummary): boolean {
    const installDate = data.supplierSystemDeliveryDate ?? data.installationDate;
    const installationDate = installDate ? serverDateTimeToLocal(installDate) : null;

    return installationDate != null && installationDate < DateTime.now().plus({ days: 3 }).toJSDate();
  }

  protected getBody(): Snippet {
    // Near-deadline / overdue rows are tinted by the table itself via the
    // semantic row-variant hook rather than a host CSS class.
    const getRowVariant = (item: PurchaseOrderSummary) => (this.isNearDeadlineOrOverdue(item) ? 'danger' : undefined);

    return html`
      <div class="delivery-pipeline-po-widget">
        <webmodule-table
          id="delivery-pipeline-po-widget"
          fill-height
          .columns=${this.getColumns()}
          .data=${this.data}
          pageLength="20"
          .clickrows=${false}
          .elevated=${false}
          .cellBorders=${false}
          .uppercaseHeader=${false}
          .rowVariantEvent=${getRowVariant as (item: object) => string | null | undefined}
          @fetch=${(e: CustomEvent) => this.internalDataLoad(e)}
        >
        </webmodule-table>
      </div>
    `;
  }

  private async internalDataLoad(e: CustomEvent) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    if (this.branchIds === undefined) {
      this.data = [];
      this.table.rowCount = 0;
      return;
    }
    const index = (e.detail?.pageIndex as number) ?? 0;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const length = (e.detail?.pageLength as number) ?? 16;

    const results = await this.cache?.getData(
      index,
      length,
      'SupplierSystemDeliveryDate',
      false,
      this.branchIds,
      this.assignedToUserId,
      this.dateRange?.startDate ? localDateToServer(this.dateRange.startDate) : undefined,
      this.dateRange?.endDate ? localDateToServer(this.dateRange.endDate) : undefined
    );

    if (results) {
      await this.cacheBranchNames(results.purchaseOrders);
      await this.cacheAuthorNames(results.purchaseOrders);
      this.data = results.purchaseOrders;
      this.table.rowCount = results.totalCount;
    }
  }
  /**
   * Resolve the authors on this page by ID, through the shared user-profile cache.
   *
   * The author of a purchase order is a DEALER employee, in the franchisee's tenant. This used to ask
   * `supplier().getAllUsers()` for the answer, which lists the SUPPLIER's own staff - a different tenant and a
   * disjoint set of ids - so the lookup missed on every row and the column read `<unavailable>` for everyone,
   * always (bug 230785). Nothing about it could ever have worked; it was not a loading race.
   *
   * `cache().userProfile` resolves by id rather than by tenant, batches the ids it has not seen into one
   * request, and keeps them for the session, so paging back and forth costs nothing.
   */
  private async cacheAuthorNames(data: PurchaseOrderSummary[]) {
    const userIds = [...new Set(data.map(d => d.creationUserId).filter(id => id && id !== emptyGuid))];
    const uncachedUserIds = userIds.filter(id => !this.authorCache.has(id));
    if (uncachedUserIds.length === 0) {
      return;
    }

    await cache().userProfile.preFetch(uncachedUserIds);
    for (const userId of uncachedUserIds) {
      // A name we could not resolve is NOT cached, so a user added between page loads is retried rather
      // than being stuck reading `<unavailable>` for the life of the session.
      const displayValue = cache().userProfile.getLocal(userId)?.displayValue;
      if (displayValue && !isEmptyOrSpace(displayValue)) {
        this.authorCache.set(userId, displayValue);
      }
    }
  }
  private async fetchAllBranches(): Promise<Map<string, string>> {
    const branchMap = new Map<string, string>();
    const allBranches = userDataStore.allFranchiseeDetails.branches;
    if (allBranches) {
      for (const branch of allBranches) {
        branchMap.set(branch.branchId, branch.abbreviation ?? branch.name ?? tlang`<unavailable>`);
      }
    }
    return branchMap;
  }
  private async cacheBranchNames(data: PurchaseOrderSummary[]) {
    const branchIds = [...new Set(data.map(d => d.branchId).filter(id => id && id !== emptyGuid))];
    const uncachedBranchIds = branchIds.filter(id => !this.branchCache.has(id));

    if (uncachedBranchIds.length > 0) {
      const branchMap = await this.fetchAllBranches();
      for (const branchId of uncachedBranchIds) {
        const branchName = branchMap.get(branchId) || tlang`<unavailable>`;
        this.branchCache.set(branchId, branchName);
      }
    }
  }
  private async loadTerritories() {
    try {
      const result = await getApiFactory().supplier().getSupplierTerritories({});
      if (result) {
        this.territories = result.territories ?? [];
        this.territoryLinks = result.territoryFranchiseBranches ?? [];
        this.territoriesLoaded = true;
      }
    } catch (error) {
      this.territories = [];
      this.territoryLinks = [];
      this.territoriesLoaded = false;
      // A lookup that failed silently is how the Author column went unnoticed. This one leaves a trace.
      console.error('Delivery pipeline: could not load supplier territories', error);
    }
  }
}
